import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, Share2, Bookmark, Sun, Moon } from "lucide-react";
import HeaderMain from "../dasboard-component/Components/HeaderMain";
import styles from "./ViewArticle.module.css";
import ImageModal from "../common/FullScreenImageModal";

const ViewArticle = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [activeSection, setActiveSection] = useState("description");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [modalInitialIndex, setModalInitialIndex] = useState(0);
  const navigate = useNavigate();
  const sectionRefs = useRef({});

  const decodeBuffer = (buffer) => {
    if (!buffer || !buffer.data || buffer.data.length === 0) return null;
    const decoded = buffer.data
      .map((byte) => String.fromCharCode(byte))
      .join("");
    return decoded === "null" ? null : decoded;
  };

  const renderTextWithLinks = (text) => {
    if (!text) return "";
    const parts = [];
    let lastIndex = 0;
    const linkRegex = /\[([^\]]+)\]\(article\/(\d+)\)/g;
    let match;

    while ((match = linkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      parts.push(
        <Link
          key={match.index}
          to={`/article/${match[2]}`}
          className={styles.articleLink}
        >
          {match[1]}
        </Link>
      );
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    return <>{parts}</>;
  };

  useEffect(() => {
    fetchArticle(id);
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "dark") setIsDarkMode(true);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.5 }
    );

    Object.values(sectionRefs.current).forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      Object.values(sectionRefs.current).forEach((section) => {
        if (section) observer.unobserve(section);
      });
    };
  }, [id, article]);

  console.log("sdcsd", process.env.REACT_APP_BASE_URL);
  const fetchArticle = async (articleId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/article/get-article/${articleId}`
      );
      if (response.ok) {
        const data = await response.json();
        setArticle({
          title: data.title,
          sportsCategory: data.sport_name || "",
          mainHeading: data.mainHeading?.heading || "",
          mainDescription: data.mainHeading?.description || "",
          mainPhotos: data.pictures
            .filter((pic) => pic.main_heading_id !== null)
            .map((pic) => ({
              photo: decodeBuffer(pic.image),
              description: "",
            })),
          subHeadings: data.subHeadings.map((sh) => ({
            heading: sh.sub_heading,
            description: sh.description,
            photos: data.pictures
              .filter((pic) => pic.sub_heading_id === sh.id)
              .map((pic) => ({
                photo: decodeBuffer(pic.image),
                description: "",
              }))
              .filter((photo) => photo.photo !== null),
          })),
          references: data.reference
            ? data.reference.map((ref) => ref.content || "")
            : [],
        });
      } else {
        console.error("Failed to fetch article");
      }
    } catch (error) {
      console.error("Error fetching article:", error);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    return `${process.env.REACT_APP_BASE_URL}/Uploads/${imagePath
      .split("\\")
      .pop()}`;
  };

  const [isOn, setIsOn] = useState(false);

  const toggleSwitch = () => setIsOn(!isOn);
  const container = {
    width: 50,
    height: 20,
    backgroundColor: "#e0e0e0",
    borderRadius: 50,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: 2,
    justifySelf: "center",
  };

  const handle = {
    width: 15,
    height: 15,
    backgroundColor: "#0071bc",
    borderRadius: "50%",
  };

  const handleLoginClick = () => {
    navigate("/loginContributor");
  };

  const toggleDarkMode = () => {
    toggleSwitch();
    setIsDarkMode(!isDarkMode);
    localStorage.setItem("theme", isDarkMode ? "light" : "dark");
  };

  const toggleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  const handleImageClick = (images, index = 0) => {
    setModalImages(images);
    setModalInitialIndex(index);
    setIsModalOpen(true);
  };

  const closeImageModal = () => {
    setIsModalOpen(false);
    setModalImages([]);
    setModalInitialIndex(0);
  };

  const scrollToSection = (sectionId) => {
    sectionRefs.current[sectionId]?.scrollIntoView({ behavior: "smooth" });
    setActiveSection(sectionId);
  };

  if (!article) {
    return (
      <motion.div
        className={styles.loading}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        Loading...
      </motion.div>
    );
  }

  return (
    <div
      className={`${styles.wikipediaLayout} ${
        isDarkMode ? styles.darkMode : ""
      }`}
    >
      <HeaderMain pageType={"viewarticle"} />

      <motion.button
        className={styles.mobileDarkModeToggle}
        onClick={toggleDarkMode}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {isDarkMode ? <Sun /> : <Moon />}
      </motion.button>
      <div
        className={`${styles.contentWrapper} ${
          isDarkMode ? styles.darkMode : ""
        }`}
      >
        <motion.nav
          className={styles.toc}
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h3 className={styles.tocTitle}>Contents</h3>
          <ul>
            <li>
              <button
                className={activeSection === "description" ? styles.active : ""}
                onClick={() => scrollToSection("description")}
              >
                Description
              </button>
            </li>
            {article.subHeadings.map((subHeading, index) => (
              <li key={index}>
                <button
                  className={
                    activeSection === `sub-${index}` ? styles.active : ""
                  }
                  onClick={() => scrollToSection(`sub-${index}`)}
                >
                  {subHeading.heading}
                </button>
              </li>
            ))}
            <li>
              <button
                className={activeSection === "references" ? styles.active : ""}
                onClick={() => scrollToSection("references")}
              >
                References
              </button>
            </li>
          </ul>
          <button
            className={styles.togglecontainer}
            style={{
              ...container,
              justifyContent: "flex-" + (isOn ? "start" : "end"),
            }}
            onClick={toggleDarkMode}
          >
            <motion.div
              className={styles.togglehandle}
              style={handle}
              layout
              transition={{
                type: "spring",
                visualDuration: 0.2,
                bounce: 0.2,
              }}
            />
          </button>
          <p>Dark | Light</p>
        </motion.nav>

        <main className={styles.mainContent}>
          <div style={{ position: "relative" }}>
            <motion.h1
              className={styles.mainTitle}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {article.title}
              <div className={styles.actionButtons}>
                <button
                  onClick={toggleBookmark}
                  className={styles.bookmarkButton}
                >
                  <Bookmark className={isBookmarked ? styles.bookmarked : ""} />
                </button>
                <button className={styles.shareButton}>
                  <Share2 />
                </button>
              </div>
            </motion.h1>

            {article.mainPhotos.length > 0 && article.mainPhotos[0].photo && (
              <motion.figure
                className={styles.titleImage}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <img
                  src={getImageUrl(article.mainPhotos[0].photo)}
                  alt={article.title}
                  loading="lazy"
                  onClick={() =>
                    handleImageClick(
                      article.mainPhotos.map((p) => getImageUrl(p.photo)),
                      0
                    )
                  }
                  style={{ cursor: "pointer" }}
                />
                <figcaption>
                  {article.title}. {article.sportsCategory}
                </figcaption>
              </motion.figure>
            )}
            <motion.div
              className={styles.wall}
              style={{ overflow: hovered ? "visible" : "hidden" }}
              initial={{ y: 0 }}
              animate={{
                y: [0, -5, 0, 5, 0],
                rotate: [0, 1, 0, -1, 0],
              }}
              transition={{
                repeat: Infinity,
                duration: 2.5,
                ease: "easeInOut",
              }}
            >
              <motion.div
                className={styles.card}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                initial={{ x: 0, rotate: 0 }}
                whileHover={{
                  y: -50,
                  rotate: -2,
                  transition: { type: "spring", stiffness: 300, damping: 15 },
                }}
              >
                <div className={styles.cardContent}>
                  <div className={styles.statHeader}>Key Moment</div>
                  <div className={styles.statContent}>
                    " {article.sportsCategory} Highlight "
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>

          <motion.section
            id="description"
            className={styles.mainSection}
            ref={(el) => (sectionRefs.current["description"] = el)}
            initial={{ opacity: 1, y: 0 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: false }}
          >
            <motion.div
              className={styles.parallaxLayer}
              initial={{ y: 50, opacity: 0.3 }}
              whileInView={{ y: 0, opacity: 0.1 }}
              transition={{ duration: 1, ease: "easeOut" }}
              viewport={{ once: false }}
            />
            <div className={styles.sectionContent}>
              <h3>Description</h3>
              <p>{renderTextWithLinks(article.mainDescription)}</p>
              {article.mainPhotos.length > 0 && (
                <motion.div className={styles.carouselContainer}>
                  <div className={styles.carousel}>
                    {article.mainPhotos.map(
                      (photo, index) =>
                        photo.photo && (
                          <motion.figure
                            key={index}
                            className={styles.carouselItem}
                            whileHover={{ scale: 1.05, rotate: 1 }}
                            transition={{ duration: 0.3 }}
                          >
                            <img
                              src={getImageUrl(photo.photo)}
                              alt={`${article.title} - ${index + 1}`}
                              loading="lazy"
                              onClick={() =>
                                handleImageClick(
                                  article.mainPhotos.map((p) =>
                                    getImageUrl(p.photo)
                                  ),
                                  index
                                )
                              }
                              style={{ cursor: "pointer" }}
                            />
                            <figcaption>
                              {article.title}. {article.sportsCategory}
                            </figcaption>
                          </motion.figure>
                        )
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          </motion.section>

          <AnimatePresence>
            {article.subHeadings.map((subHeading, index) => (
              <motion.section
                key={index}
                id={`sub-${index}`}
                className={styles.subSection}
                ref={(el) => (sectionRefs.current[`sub-${index}`] = el)}
                initial={{ opacity: 1, y: 0 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: false }}
              >
                <motion.div
                  className={styles.parallaxLayer}
                  initial={{ y: 50, opacity: 0.3 }}
                  whileInView={{ y: 0, opacity: 0.1 }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  viewport={{ once: false }}
                />
                {subHeading.heading && (
                  <div className={styles.sectionContent}>
                    <h3>{subHeading.heading}</h3>
                    <div className={styles.subContent}>
                      <p>{renderTextWithLinks(subHeading.description)}</p>
                      {subHeading.photos.length > 0 && (
                        <motion.div className={styles.carouselContainer}>
                          <div className={styles.carousel}>
                            {subHeading.photos.map(
                              (photo, photoIndex) =>
                                photo.photo && (
                                  <motion.figure
                                    key={photoIndex}
                                    className={styles.carouselItem}
                                    whileHover={{ scale: 1.05, rotate: 1 }}
                                    transition={{ duration: 0.3 }}
                                  >
                                    <img
                                      src={getImageUrl(photo.photo)}
                                      alt={`${subHeading.heading} - ${
                                        photoIndex + 1
                                      }`}
                                      loading="lazy"
                                      onClick={() =>
                                        handleImageClick(
                                          subHeading.photos.map((p) =>
                                            getImageUrl(p.photo)
                                          ),
                                          photoIndex
                                        )
                                      }
                                      style={{ cursor: "pointer" }}
                                    />
                                    <figcaption>
                                      {subHeading.heading}
                                    </figcaption>
                                  </motion.figure>
                                )
                            )}
                          </div>
                        </motion.div>
                      )}
                    </div>
                  </div>
                )}
              </motion.section>
            ))}
          </AnimatePresence>

          <motion.section
            id="references"
            className={styles.references}
            ref={(el) => (sectionRefs.current["references"] = el)}
            initial={{ opacity: 1, y: 0 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: false }}
          >
            <motion.div
              className={styles.parallaxLayer}
              initial={{ y: 50, opacity: 0.3 }}
              whileInView={{ y: 0, opacity: 0.1 }}
              transition={{ duration: 1, ease: "easeOut" }}
              viewport={{ once: false }}
            />
            <div className={styles.sectionContent}>
              <h2>References</h2>
              <div className={styles.referenceCards}>
                {article.references.map((ref, index) => (
                  <motion.div key={index} className={styles.referenceCard}>
                    <p>{renderTextWithLinks(ref)}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.section>

          <motion.button
            className={styles.scrollTop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <ArrowUp size={16} />
          </motion.button>
        </main>
      </div>

      <ImageModal
        images={modalImages}
        initialIndex={modalInitialIndex}
        isOpen={isModalOpen}
        onClose={closeImageModal}
        alt="Article Image"
        isBase64={false}
      />
    </div>
  );
};

export default ViewArticle;
